import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { OrganizationType } from 'data-model';

export enum Severity {
  INFO = 'INFO',
  WARNING = 'WARNING',
}

export type Message = {
  content: string;
};

export type BannerNotificationCreateInput = {
  severity: Severity;
  start: Date;
  end: Date;
  audienceId: string;
  messages: { [key: string]: string };
  version?: string;
};

export type BannerNotificationCreateResponse = {
  id: string;
  severity: Severity;
  start: Date;
  end: Date;
  audience: Audience;
  message: Message;
};

export enum AudienceType {
  LEISTUNG = 'LEISTUNG',
  PLATFORM = 'PLATFORM',
}

export type Audience = {
  id: string;
  type: AudienceType;
  name: string;
  sourceId: string;
};

export type Notification = {
  id: string;
  startDate: string;
  endDate: string;
  message: Message;
  severity?: Severity;
  audience?: Audience;
};

export enum NotificationType {
  BANNER = 1,
  PLATFORM = 0,
}

/**
 * Type for the 'Cartridge' data
 */
export type NotificationCartridge = {
  id: string;
  contentId?: string;
  name: {
    [key: string]: string;
  };
  description: string;
  longDescription: string;
  icon?: string;
  image?: string;
  version?: number;
  variants: NotificationCartridgeVariant[];
  status: NotificationCartridgeStatus;
  bookedVariantId?: string;
  //@deprecated
  remoteEntry: string;
};

export type NotificationCartridgeVariant = {
  variantId: string;
  contentId?: string;
  name: {
    [key: string]: string;
  };
  description: string;
  longDescription: string;
  icon?: string;
  image?: string;
  price: Price;
  remoteEntry?: string;
  contractPeriod?: number;
  variantStatus: NotificationCartridgeStatus;
  bookingId?: string;
  allowUiBooking: boolean;
};

export interface PlatformAdminBannerNotificationsData {
  hasNext: boolean;
  pageIndex: number;
  pageSize: number;
  result: BannerNotification[];
  totalCount: number;
}

export type BannerNotification = {
  id: string;
  audience: string;
  editable: boolean;
  end: string;
  languages: string[];
  messages: {
    [key: string]: string;
  };
  start: string;
  version?: string;
};

export type BannerNotificationDetails = {
  id: string;
  audience: string;
  editable: boolean;
  endDate: string;
  languages: string[];
  messages: {
    [key: string]: string;
  };
  startDate: string;
  version?: string;
};

export type PriceModel = 'SUBSCRIPTION' | 'PAY_PER_USE' | 'FIXED_PRICE';
export type BillingPeriod = 'NONE' | 'QUARTERLY' | 'MONTHLY' | 'YEARLY' | 'HALF_YEARLY';
export type Price = {
  currency: string; // ISO 3-Letter Currency Code
  centAmount: number;
  priceModel: PriceModel;
  billingPeriod: BillingPeriod;
  organizationType?: OrganizationType;
};

export enum NotificationCartridgeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(private http: HttpClient) {}

  createBannerNotification(
    bannerNotification: BannerNotificationCreateInput
  ): Observable<BannerNotificationCreateResponse> {
    return this.http.post<BannerNotificationCreateResponse>(
      AppConfigService.appConfiguration.apiUrl['notificationServiceBannerNotifications'],
      bannerNotification
    );
  }

  // Retrieves banner notifications
  getBannerNotificationById(id: string): Observable<BannerNotificationDetails> {
    return this.http.get<BannerNotificationDetails>(
      `${AppConfigService.appConfiguration.apiUrl['notificationServiceBannerNotifications']}/${id}`
    );
  }

  // Delete notification only for platform admins.
  deleteBannerNotification(id: string): Observable<void> {
    return this.http.delete<void>(
      `${AppConfigService.appConfiguration.apiUrl['notificationServiceBannerNotifications']}/${id}`
    );
  }

  // Update notification only for platform admins.
  updateBannerNotification(id: string, data: BannerNotificationCreateInput): Observable<BannerNotificationCreateInput> {
    return this.http.put<BannerNotificationCreateInput>(
      `${AppConfigService.appConfiguration.apiUrl['notificationServiceBannerNotifications']}/${id}`,
      data
    );
  }

  getAvailableAudiences(): Observable<Audience[]> {
    return this.http.get<Audience[]>(AppConfigService.appConfiguration.apiUrl['notificationServiceAudiences']);
  }

  getActiveBannerNotifications(): Observable<Notification[]> {
    const now: string = new Date().toISOString();
    return this.http.get<Notification[]>(
      AppConfigService.appConfiguration.apiUrl['notificationServiceBannerNotifications'] + '?datetime=' + now
    );
  }

  // Retrieves banner notifications
  getAllPlatformAdminBannerNotifications(
    pageIndex: number,
    pageSize: number
  ): Observable<PlatformAdminBannerNotificationsData> {
    const now: string = new Date().toISOString();
    return this.http.get<PlatformAdminBannerNotificationsData>(
      `${AppConfigService.appConfiguration.apiUrl['notificationServiceBannerNotifications']}/platform-admin?page=${pageIndex}&size=${pageSize}&editableFrom=${now}`
    );
  }

  /**
   * Get a list of all cartridges for user with their variants. (Entries with not 'ACTIVE' status and allowUiBooking='false' are omitted)
   *
   * @returns {Observable<NotificationCartridge[]>} - NotificationCartridge list without apis and widgets attribute
   */
  getCartridgesForNotificationCreation(): Observable<NotificationCartridge[]> {
    return this.http.get<NotificationCartridge[]>(
      `${AppConfigService.appConfiguration.apiUrl['leistungService']}?scope=admin`
    );
  }
}
